
import { Vue, Component, Prop, Watch } from "vue-property-decorator";

import GET_SKILLS from "@/apollo/autocomplete-queries/GetSkills.gql";

import DropdownFilter from "@/components/filters/DropdownFilterNew.vue";

import { Query_RootSkillArgs } from "@/generated/graphql";
import filterMultiselectItems from "@/helpers/multiselect/filterMultiselectItems";
import SkillMultiselectOption from "@/entities/SkillMultiselectOption";

@Component({
  components: {
    DropdownFilter,
  },
  apollo: {
    skills: {
      query: GET_SKILLS,
      variables() {
        return this.skillAutocompleteFilter;
      },
      update(data) {
        return data.skills_short_name_first;
      },
      skip() {
        return !this.skillSearchString;
      },
    },
  },
})
export default class SkillsMultiselect extends Vue {
  @Prop({ type: Array, required: true }) readonly value!: string[];
  @Prop({ type: Boolean, default: true }) readonly withBorder!: boolean;
  @Prop({ type: Number, default: 5 }) readonly limit!: number;
  @Prop({ type: String, default: "" }) readonly pillsColor: string;
  @Prop({ type: Number, default: undefined }) readonly max?: number;
  @Prop({ type: String, default: "" }) readonly getcontainerStyle?: string;
  @Prop({ type: String, default: undefined }) readonly getParentStyle?: string;
  @Prop({ type: Object, default: () => ({}) }) readonly condition!: Object;
  @Prop({ type: String, default: undefined }) readonly currentUserId?: string;
  @Prop({ type: String, default: undefined }) readonly companyId?: string;
  @Prop({ type: Boolean, default: false })
  readonly enableSkillsAdding!: boolean;
  @Prop({ type: Boolean, default: false }) readonly withCustomSkills!: boolean;
  @Prop({ type: Array, required: true })
  readonly previouslyChosenSkillsIds!: number[];
  @Prop({ type: Boolean, default: false }) readonly disabled!: boolean;
  @Prop({ type: String, default: "Enter Tag" }) readonly placeholder: string;

  skills: SkillMultiselectOption[] = [];
  skillSearchString: string = "";
  chosenSkills: SkillMultiselectOption[] = [];
  newSkillsNames: string[] = [];

  get skillsOptions(): SkillMultiselectOption[] {
    if (!this.withCustomSkills) {
      return this.skills;
    }

    const chosenSkillsNames = this.chosenSkills.map(({ name }) => name);

    const searchSkillAlreadyChosen = [
      ...chosenSkillsNames,
      ...this.newSkillsNames,
    ].find(
      (skillName) =>
        skillName?.toLowerCase() === this.skillSearchString?.toLowerCase()
    );

    if (searchSkillAlreadyChosen || this.skillFoundInList) {
      return this.skills;
    }

    return [
      {
        id: 0 - Date.now(),
        name: this.skillSearchString,
        company_id: null,
        user_id: null,
      },
      ...this.skills,
    ];
  }

  get computedCustomStyles(): string {
    const styles = [this.getParentStyle, this.getcontainerStyle].filter(
      (style) => !!style
    );
    return styles.join(" ");
  }

  get skillAutocompleteFilter(): Query_RootSkillArgs {
    const conditions: any[] = [];

    if (this.skillSearchString) {
      conditions.push({
        name: {
          _ilike: `%${this.skillSearchString}%`,
        },
      });
    }

    if (this.companyId) {
      conditions.push({
        _or: [
          { company_id: { _eq: this.companyId } },
          { company_id: { _is_null: true } }
        ]
      });
    }

    if (this.value.length) {
      conditions.push({
        id: {
          _nin: this.value.filter(id => Number(id) >= 0),
        },
      });
    }

    return {
      where: conditions.length > 0 ? { _and: [...conditions] } : this.condition,
      limit: this.limit,
    };
  }

  @Watch("value", { immediate: true })
  async onSkillsIdsChanged(): Promise<void> {
    const skillsIds = this.value;

    if (skillsIds.length === 0) {
      this.chosenSkills = [];
      return;
    }

    const {
      newItemsIds: newSkillIds,
      previouslyChosenItems: previouslyChosenSkills,
    } = filterMultiselectItems(skillsIds, this.chosenSkills);
    if (newSkillIds.length === 0) {
      return;
    }

    const rawData = await this.$apollo.query({
      query: GET_SKILLS,
      variables: {
        where: {
          id: {
            _in: newSkillIds,
          },
        },
      },
    });
    const newChosenSkills = rawData.data.skills_short_name_first;
    this.chosenSkills = [...previouslyChosenSkills, ...newChosenSkills];
  }

  get skillFoundInList(): boolean {
    return this.skills.some(({ name }) => name === this.skillSearchString);
  }

  updateSearch(searchValue: string): void {
    this.skillSearchString = searchValue;
    this.$emit("search", searchValue);
  }

  isNewSkill(skill: SkillMultiselectOption): boolean {
    if (skill.id >= 0) {
      return false;
    }

    return (
      !this.skillFoundInList ||
      this.newSkillsNames.some((skillName) => skillName === skill.name)
    );
  }

  handleInput(chosenSkills: SkillMultiselectOption[]): void {
    this.chosenSkills = chosenSkills;

    this.$emit("inputSkills", chosenSkills);
    if (!this.withCustomSkills) {
      const chosenSkillsIds = chosenSkills.map((option) => Number(option.id));

      this.$emit("input", chosenSkillsIds);
      return;
    }

    const chosenSkillsIds = chosenSkills
      .filter((chosenSkill) => chosenSkill.id >= 0)
      .map((option) => Number(option.id));

    this.$emit("input", chosenSkillsIds, chosenSkills);
  }
}
